import React, { useState, useEffect, useContext } from "react"
import {
  Button,
  Col,
  Form,
  Input,
  Label,
  Row,
  UncontrolledAlert,
  Card,
  CardBody,
  Modal,
} from "reactstrap"
import TenantConnectionContext from "./TenantConnectionContext"
import LoadingOverlay from "react-loading-overlay"
import Error from "pages/NotificationModal/Error"
import Success from "./Success"
import Confirmation from "./Confirmation"
import SecretInput from "./SecretInput"

// TAI (TAI TMS) outbound auth: status auth is an "Api-Key" token (method=ApiToken,
// x-api-key header) + the status endpoint url. TAI also carries a reference-number
// config (config.refNumConfig): its own apiKey (for the TAI Shipping API), the
// shipment URL, and an updateApptConfNumber flag. This form edits url + token +
// refNumConfig; method (ApiToken) and username are preserved server-side.
const isMasked = v => typeof v === "string" && /^(\*{1,2}|.\*\*.)$/.test(v)
const hasVal = v => v != null && String(v).trim() !== ""

const TaiAuthForm = ({ hideForm }) => {
  const {
    loading,
    success,
    error,
    errorMessage,
    setSuccess,
    setError,
    tenantData,
    submitUpdate,
    revealSecret,
    updateResponse,
  } = useContext(TenantConnectionContext)

  const [url, setUrl] = useState("")
  const [token, setToken] = useState("")
  const [apiKey, setApiKey] = useState("")
  const [shipmentUrl, setShipmentUrl] = useState("")
  const [updateApptConfNumber, setUpdateApptConfNumber] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    if (!tenantData) return
    setUrl(tenantData.url || "")
    setToken(tenantData.token || "")
    const rn = tenantData.refNumConfig || {}
    setApiKey(rn.apiKey || "")
    setShipmentUrl(rn.shipmentUrl || "")
    setUpdateApptConfNumber(!!rn.updateApptConfNumber)
  }, [tenantData])

  const fieldRow = (label, node) => (
    <Row className="mb-2" key={label}>
      <Label className="col-sm-3 col-form-label">{label}</Label>
      <Col sm={6} className="ms-4">
        {node}
      </Col>
    </Row>
  )

  const handleSubmit = () => {
    setErrorMsg("")
    const missing = []
    if (!hasVal(url)) missing.push("URL")
    if (!(hasVal(token) || isMasked(token))) missing.push("API Token")
    if (!(hasVal(apiKey) || isMasked(apiKey))) missing.push("Reference API Key")
    if (!hasVal(shipmentUrl)) missing.push("Shipment URL")
    if (missing.length) {
      setErrorMsg(`Required: ${missing.join(", ")}`)
      return
    }
    setShowConfirmation(true)
  }

  const handleConfirmSubmit = async () => {
    setShowConfirmation(false)
    await submitUpdate({
      url,
      token,
      refNumConfig: {
        apiKey,
        shipmentUrl,
        updateApptConfNumber,
      },
    })
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    hideForm()
  }
  const closeSuccess = () => {
    setSuccess(false)
    hideForm()
  }
  const togError = () => setError(false)

  if (!tenantData) {
    return (
      <LoadingOverlay active={loading} spinner>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <h5>Loading...</h5>
        </div>
      </LoadingOverlay>
    )
  }

  return (
    <>
      <Modal isOpen={success} toggle={closeSuccess} centered size="md">
        <Success data={updateResponse} cancel={closeSuccess} />
      </Modal>
      <Modal isOpen={error} toggle={togError} centered size="sm">
        <Error
          message={errorMessage || "Something went wrong"}
          closeModal={togError}
        />
      </Modal>
      <Modal
        isOpen={showConfirmation}
        toggle={() => setShowConfirmation(false)}
        centered
        size="sm"
      >
        <Confirmation
          message="Do you want to save these changes?"
          confirm={handleConfirmSubmit}
          cancel={() => setShowConfirmation(false)}
        />
      </Modal>

      <LoadingOverlay active={loading} spinner>
        <Card className="p-3">
          <div className="mb-3">
            <h6 className="text-primary">Edit TAI (TAI TMS) Outbound Auth</h6>
          </div>
          <CardBody className="p-0">
            <Card outline color="primary" className="border mt-2 mb-3">
              <CardBody className="p-3">
                {errorMsg && (
                  <div className="mb-3">
                    <UncontrolledAlert color="danger" role="alert">
                      {errorMsg}
                    </UncontrolledAlert>
                  </div>
                )}
                <Form>
                  {fieldRow(
                    "URL",
                    <Input
                      type="text"
                      value={url}
                      placeholder="Status update endpoint"
                      onChange={e => setUrl(e.target.value)}
                    />
                  )}
                  {fieldRow(
                    "API Token (Api-Key header)",
                    <SecretInput
                      value={token}
                      onChange={setToken}
                      label="API Token"
                      placeholder="Enter api token"
                      revealPath="token"
                      onReveal={revealSecret}
                    />
                  )}

                  <Row className="mb-1 mt-3">
                    <Col sm={9} className="ms-4">
                      <small className="text-muted">
                        Reference Number Config — the TAI Shipping API used to write
                        reference numbers back to shipments.
                      </small>
                    </Col>
                  </Row>
                  {fieldRow(
                    "Reference API Key",
                    <SecretInput
                      value={apiKey}
                      onChange={setApiKey}
                      label="Reference API Key"
                      placeholder="Enter reference api key"
                      revealPath="refNumConfig.apiKey"
                      onReveal={revealSecret}
                    />
                  )}
                  {fieldRow(
                    "Shipment URL",
                    <Input
                      type="text"
                      value={shipmentUrl}
                      placeholder="https://tms.taicloud.net/PublicApi/Shipping/v2/Shipments"
                      onChange={e => setShipmentUrl(e.target.value)}
                    />
                  )}
                  {fieldRow(
                    "Update Appt Confirmation #",
                    <div className="form-check mt-2">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="tai-update-appt-conf"
                        checked={updateApptConfNumber}
                        onChange={e => setUpdateApptConfNumber(e.target.checked)}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="tai-update-appt-conf"
                      >
                        Write appointment confirmation number to the TAI shipment
                        reference
                      </Label>
                    </div>
                  )}
                </Form>
              </CardBody>
            </Card>

            <div className="d-flex justify-content-end mt-3 me-4">
              <div className="d-flex gap-2">
                <Button onClick={handleSubmit} className="btn" color="primary">
                  Save
                </Button>
                <Button
                  onClick={handleCancel}
                  className="btn"
                  color="secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </LoadingOverlay>
    </>
  )
}

export default TaiAuthForm
